import _ from 'lodash';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { getLevelUpStudents } from '../../api/semesters.api';
import Button from '../../components/Button';
import LevelUpTable from '../../components/class/level-up/LevelUpTable';
import DownloadButton from '../../components/DownloadButton';
import ErrorResponse from '../../components/ErrorResponse';
import SimpleHeader from '../../components/layout/SimpleHeader';
import Loader from '../../components/Loader';
import Pagination from '../../components/Pagination';
import SelectCampus from '../../components/SelectCampus';
import SelectLevel from '../../components/SelectLevel';
import SelectSemester from '../../components/SelectSemester';
import useAxios from '../../hooks/useAxios';
import useNotification from '../../hooks/useNotification';
import config from '../../lib/config';
import exportTable from '../../lib/exportTable';
import { getCommonCodeLabel } from '../../lib/store';
import {
  IGetLevelUpStudents,
  ILevelUpStudent,
} from '../../models/semesters.model';
import { RootState } from '../../store/rootReducer';
import moment from 'moment';
import { TableColumnsType } from 'antd';
import TableEmpty from '../../components/common/TableEmpty';

const ClassLevelUp = () => {
  const { t } = useTranslation();
  const { currentCampus } = useSelector((state: RootState) => state.auth);
  const { infoNotification } = useNotification();
  const {
    data: res,
    isLoading,
    error,
    execute,
  } = useAxios<IGetLevelUpStudents>();
  // for excel download
  const {
    data: res2,
    isLoading: isLoading2,
    error: error2,
    execute: execute2,
  } = useAxios<IGetLevelUpStudents>();

  const [isFirstRun, setIsFirstRun] = useState(true);
  const [pageNo, setPageNo] = useState(1);
  const [semesterCode, setSemesterCode] = useState<number>();
  const [courseCode, setCourseCode] = useState<string>();

  useEffect(() => {
    if (isFirstRun) {
      setIsFirstRun(false);
      return;
    }

    doGetLevelUpStudents();
  }, [pageNo]);

  useEffect(() => {
    if (res2 && !isLoading2) {
      const data = _.cloneDeep(res2?.data);
      data.forEach((item: any) => {
        item.icoachingAvgScore = item.icoachingAvgScore
          ? Math.round(item.icoachingAvgScore)
          : '';
        item.elearningAvgScore = item.elearningAvgScore
          ? Math.round(item.elearningAvgScore)
          : '';
        item.totalTestScore = item?.totalTestScore?.toFixed(1) || '';
        item.icoachingConversionScore = item.icoachingConversionScore
          ? Math.round(item.icoachingConversionScore)
          : '';
        item.elearningConversionScore = item.elearningConversionScore
          ? Math.round(item.elearningConversionScore)
          : '';
        item.totalConversionScore =
          item?.totalConversionScore?.toFixed(1) || '';
        item.levelUpResultCode = getCommonCodeLabel(
          'GR0005',
          item.levelUpResultCode,
          'ko',
        );
        item.campusLevelUpResultCode = getCommonCodeLabel(
          'GR0005',
          item.campusLevelUpResultCode,
          'ko',
        );
      });
      const currentDate = moment(new Date());
      exportTable(
        columns,
        data,
        `${t('class.levelUp.1')}_(${currentDate.format('YYYYMMDD')})`,
        t('class.levelUp.1'),
        [
          12, 10, 20, 15, 12, 15, 12, 15, 12, 15, 12, 15, 12, 15, 12, 20, 12, 12, 12,
        ],
      );
    }
  }, [res2, isLoading2]);

  const doGetLevelUpStudents = (_pageNo?: number) => {
    if (!currentCampus) {
      return infoNotification(t('class.class.message3'));
    }
    if (!semesterCode) {
      return infoNotification(t('class.class.message4'));
    }
    if (!courseCode) {
      return infoNotification(t('class.levelUp.message1'));
    }
    execute(
      getLevelUpStudents({
        campusCode: currentCampus,
        semesterCode,
        courseCode,
        pageNo: _pageNo || pageNo,
        perPage: config.pageSize,
      }),
    );
  };

  const onExportTable = () => {
    execute2(
      getLevelUpStudents({
        campusCode: currentCampus,
        semesterCode,
        courseCode,
        pageNo: 1,
        perPage: 9999999,
      }),
    );
  };

  const columns: TableColumnsType<ILevelUpStudent> = [
    {
      title: t('student.student.76'),
      dataIndex: 'campusKorName',
    },
    {
      title: t('message.write.1'),
      dataIndex: 'memberKorName',
    },
    {
      title: t('sms.send.4'),
      dataIndex: 'className',
    },
    {
      title: t('program.semester.19'),
      dataIndex: 'monthlyTestScore1',
    },
    {
      title: t('program.semester.20'),
      dataIndex: 'monthlyTestConversionScore1',
    },
    {
      title: t('program.semester.21'),
      dataIndex: 'monthlyTestScore2',
    },
    {
      title: t('program.semester.22'),
      dataIndex: 'monthlyTestConversionScore2',
    },
    {
      title: t('program.semester.23'),
      dataIndex: 'levelTestScore',
    },
    {
      title: t('program.semester.24'),
      dataIndex: 'levelTestConversionScore',
    },
    {
      title: 'iCoaching 원점수',
      dataIndex: 'icoachingAvgScore',
    },
    {
      title: 'iCoaching 환산',
      dataIndex: 'icoachingConversionScore',
    },
    {
      title: t('program.semester.25'),
      dataIndex: 'elearningAvgScore',
    },
    {
      title: t('program.semester.26'),
      dataIndex: 'elearningConversionScore',
    },
    {
      title: t('program.semester.27'),
      dataIndex: 'totalTestScore',
    },
    {
      title: t('program.semester.28'),
      dataIndex: 'totalConversionScore',
    },
    {
      title: t('class.course.count'),
      dataIndex: 'studentRegisterCount'
    },
    {
      title: t('program.semester.29'),
      dataIndex: 'levelUpResultCode',
    },
    {
      title: t('program.semester.30'),
      dataIndex: 'campusLevelUpResultCode',
    },
    {
      title: t('program.semester.31'),
      dataIndex: 'nextCourseName',
    },
  ];

  return (
    <>
      <div className="common-search">
        <div className="search-area">
          <ul className="search-bx">
            <li>
              <label>{t('common.menu.campus')}</label>
              <div>
                <SelectCampus />
              </div>
            </li>
            <li>
              <label>{t('class.class.placeholder4')}</label>
              <div>
                <SelectSemester
                  semesterCode={semesterCode}
                  setSemesterCode={setSemesterCode}
                />
              </div>
            </li>
            <li>
              <label>{t('class.levelUp.placeholder1')}</label>
              <div>
                <SelectLevel
                  value={courseCode}
                  onChange={(value) => setCourseCode(value)}
                />
              </div>
              <div className="comm-btn__area">
                <Button
                  className="btn-basic small black"
                  onClick={() => doGetLevelUpStudents(1)}
                  text={t('common.button.search')}
                />
              </div>
            </li>
          </ul>
        </div>
      </div>
      <div className="content scrollbar">
        {(isLoading || isLoading2) && <Loader />}
        <ErrorResponse error={error} />
        <ErrorResponse error={error2} />
        <SimpleHeader
          parentMenu={t('common.menu.class')}
          currentMenu={t('class.levelUp.title1')}
          onlyTitle={true}
        />

        <div>
          {res?.data?.length ? (
            <>
              <div className="comm-table__area line-type">
                <LevelUpTable
                  data={res?.data}
                  doGetLevelUpStudents={doGetLevelUpStudents}
                />
              </div>
              <div className="comm-bx__between">
                <DownloadButton onClick={onExportTable} />
                <div className="common-paging center">
                  <Pagination
                    count={res?.total || 0}
                    page={pageNo}
                    setPage={setPageNo}
                  />
                </div>
                <div />
              </div>
            </>
          ) : (
            <TableEmpty text={t('statistics.appday.text1')} />
          )}
        </div>
      </div>
    </>
  );
};

export default ClassLevelUp;
